import { authAPI, userAPI } from './api';

// Cache for user data to avoid excessive API calls
// We use in-memory cache but ensure we always validate with the server when needed
let userCache = null;
let isAuthenticatedCache = null;
let authCheckPromise = null; // Promise to track ongoing auth checks
let userFetchPromise = null; // Promise to track ongoing user data fetch

const AuthService = {
  login: async (email, password) => {
    try {
      const response = await authAPI.login({ email, password });
      const { user } = response.data;

      // Update the cache with the user data
      userCache = user;
      isAuthenticatedCache = true;
      // Reset promises
      authCheckPromise = null;
      userFetchPromise = null;

      return user;
    } catch (error) {
      throw error;
    }
  },

  register: async (userData) => {
    try {
      const response = await authAPI.register(userData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  logout: async () => {
    try {
      // Call the logout endpoint to clear the cookie
      await userAPI.logout();
      // Clear the cache
      userCache = null;
      isAuthenticatedCache = false;
      // Reset promises
      authCheckPromise = null;
      userFetchPromise = null;

      // Clear any local storage that might be used
      localStorage.removeItem('redirectAfterLogin');

      // Use replace instead of href to avoid adding to browser history
      window.location.replace('/login');
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear cache even if the request fails
      userCache = null;
      isAuthenticatedCache = false;
      // Reset promises
      authCheckPromise = null;
      userFetchPromise = null;

      // Clear any local storage that might be used
      localStorage.removeItem('redirectAfterLogin');

      // Use replace instead of href to avoid adding to browser history
      window.location.replace('/login');
    }
  },

  getCurrentUser: async () => {
    // If we have a cached user, return it
    if (userCache) {
      return userCache;
    }

    // If there's already a user fetch in progress, wait for it
    if (userFetchPromise) {
      return userFetchPromise;
    }

    // Start a new user fetch
    userFetchPromise = (async () => {
      try {
        const response = await userAPI.getCurrentUser();
        userCache = response.data;
        return userCache;
      } catch (error) {
        console.error('Error fetching current user:', error);
        return null;
      } finally {
        // Clear the promise so future fetches can run
        userFetchPromise = null;
      }
    })();

    return userFetchPromise;
  },

  // Synchronous version for components that can't use async/await
  getCurrentUserSync: () => {
    return userCache;
  },

  isAuthenticated: async () => {
    // If we have a cached authentication status and it's true, return it
    // If it's false, we'll still check with the server to see if it's changed
    if (isAuthenticatedCache === true) {
      return true;
    }

    // If there's already an authentication check in progress, wait for it
    if (authCheckPromise) {
      return authCheckPromise;
    }

    // Start a new authentication check
    authCheckPromise = (async () => {
      try {
        // Make a request to a protected endpoint to verify the cookie is valid
        // This will use the HTTP-only cookie automatically
        await userAPI.validateAuth();
        isAuthenticatedCache = true;
        return true;
      } catch (error) {
        isAuthenticatedCache = false;
        return false;
      } finally {
        // Clear the promise so future checks can run
        authCheckPromise = null;
      }
    })();

    return authCheckPromise;
  },

  isAdmin: async () => {
    // First check if we're authenticated
    const isAuth = await AuthService.isAuthenticated();
    if (!isAuth) {
      return false;
    }

    // Then get the user data
    const user = await AuthService.getCurrentUser();
    return user && user.role === 'ADMIN';
  },

  // Synchronous version for components that can't use async/await
  isAdminSync: () => {
    return userCache && userCache.role === 'ADMIN';
  },

  isSuperAdmin: async () => {
    // First check if we're authenticated
    const isAuth = await AuthService.isAuthenticated();
    if (!isAuth) {
      return false;
    }

    // Then get the user data
    const user = await AuthService.getCurrentUser();
    return user && user.role === 'SUPERADMIN';
  },

  // Synchronous version for components that can't use async/await
  isSuperAdminSync: () => {
    return userCache && userCache.role === 'SUPERADMIN';
  }
};

export default AuthService;
